/**
 * Mon carnet de rencontres.
 *
 * « À tel événement, qui j'ai rencontré, les notes, les prochaines actions, et
 * est-ce que j'ai bien ses coordonnées. Je ne dois JAMAIS perdre ça. »
 * (Tom, 2026-09-17)
 *
 * D'où la règle de l'écran : on écrit d'abord sur l'appareil (carnetLocal.ts),
 * on envoie ensuite. Rien n'est jamais bloqué par un champ manquant ni par un
 * réseau absent, et l'état de la sauvegarde est visible en permanence.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpRight, Check, ChevronDown, Download, Link2, Mail, NotebookPen, Phone, Plus, Search, Trash2, X,
} from 'lucide-react';
import { appelerApi, envoyer } from '../../reseau.ts';
import { Badge, Btn, Card, PageTitle, SectionLabel } from '../../admin/ui.tsx';
import { Champ } from './MonEntreprise.tsx';
import { PhotosRencontre } from '../../composants/PhotosRencontre.tsx';
import {
  brouillon, brouillons, estLocale, fusionner, noteVide, oublierBrouillon, poserBrouillon,
  renommerBrouillon, versApi, type ActionNote, type NoteCarnet, type PhotoNote,
} from './carnetLocal.ts';

type Etat = 'repos' | 'envoi' | 'ok' | 'attente';

type EvenementCarnet = { id: string; nom: string; debut_le: string };
type ActionAFaire = { id: string; texte: string; echeance: string | null; note_id: string; nom: string | null; entreprise: string | null; evenement_nom: string | null };

export function Carnet() {
  const [notes, setNotes] = useState<NoteCarnet[] | null>(null);
  const [evenements, setEvenements] = useState<EvenementCarnet[]>([]);
  const [aFaire, setAFaire] = useState<ActionAFaire[]>([]);
  const [q, setQ] = useState('');
  const [filtreEvenement, setFiltreEvenement] = useState('');
  const [ouverte, setOuverte] = useState<string | null>(null);
  const [etats, setEtats] = useState<Record<string, Etat>>({});

  const charger = useCallback(async () => {
    const params = new URLSearchParams();
    if (filtreEvenement) params.set('evenementId', filtreEvenement);
    if (q.trim()) params.set('q', q.trim());
    const [r, e, a] = await Promise.all([
      appelerApi<{ notes: NoteCarnet[] }>(`/api/carnet/notes?${params}`),
      appelerApi<{ evenements: EvenementCarnet[] }>('/api/carnet/evenements'),
      appelerApi<{ actions: ActionAFaire[] }>('/api/carnet/actions'),
    ]);
    // Même si l'API ne répond pas, on montre les brouillons locaux : une note
    // prise hors ligne doit rester sous les yeux.
    setNotes(fusionner(r.donnees?.notes ?? []));
    if (e.donnees) setEvenements(e.donnees.evenements);
    if (a.donnees) setAFaire(a.donnees.actions);
  }, [q, filtreEvenement]);

  useEffect(() => {
    void charger();
  }, [charger]);

  /** Envoie une note au serveur ; garde le brouillon tant que ce n'est pas confirmé. */
  const synchroniser = useCallback(async (id: string) => {
    const note = brouillon(id);
    if (!note) return;
    setEtats((x) => ({ ...x, [id]: 'envoi' }));

    if (estLocale(id)) {
      const r = await envoyer<{ id: string }>('/api/carnet/notes', versApi(note));
      if (!r.donnees) {
        setEtats((x) => ({ ...x, [id]: 'attente' }));
        return;
      }
      renommerBrouillon(id, r.donnees.id);
      oublierBrouillon(r.donnees.id);
      setNotes((liste) => (liste ?? []).map((n) => (n.id === id ? { ...n, id: r.donnees!.id } : n)));
      setOuverte((o) => (o === id ? r.donnees!.id : o));
      setEtats((x) => ({ ...x, [r.donnees!.id]: 'ok' }));
      return;
    }

    const r = await envoyer(`/api/carnet/notes/${id}`, versApi(note), 'PUT');
    if (r.erreur) {
      setEtats((x) => ({ ...x, [id]: 'attente' }));
      return;
    }
    oublierBrouillon(id);
    setEtats((x) => ({ ...x, [id]: 'ok' }));
  }, []);

  // Une note en attente repart dès qu'on revient en ligne, et toutes les
  // minutes — sans que personne ait à y penser.
  useEffect(() => {
    const reprendre = () => brouillons().forEach((n) => void synchroniser(n.id));
    window.addEventListener('online', reprendre);
    const minuterie = setInterval(reprendre, 60_000);
    reprendre();
    return () => {
      window.removeEventListener('online', reprendre);
      clearInterval(minuterie);
    };
  }, [synchroniser]);

  const minuteries = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  /** Toute modification : état à l'écran, brouillon sur l'appareil, envoi différé. */
  const modifier = useCallback(
    (id: string, patch: Partial<NoteCarnet>) => {
      setNotes((liste) => {
        const suite = (liste ?? []).map((n) => (n.id === id ? { ...n, ...patch } : n));
        const note = suite.find((n) => n.id === id);
        if (note) poserBrouillon(note);
        return suite;
      });
      setEtats((x) => ({ ...x, [id]: 'repos' }));
      clearTimeout(minuteries.current[id]);
      minuteries.current[id] = setTimeout(() => void synchroniser(id), 900);
    },
    [synchroniser],
  );

  const ajouter = () => {
    const note = noteVide(filtreEvenement || null);
    poserBrouillon(note);
    setNotes((liste) => [note, ...(liste ?? [])]);
    setOuverte(note.id);
    setEtats((x) => ({ ...x, [note.id]: 'repos' }));
    void synchroniser(note.id);
  };

  const supprimer = async (id: string) => {
    oublierBrouillon(id);
    setNotes((liste) => (liste ?? []).filter((n) => n.id !== id));
    setOuverte(null);
    if (!estLocale(id)) await envoyer(`/api/carnet/notes/${id}`, {}, 'DELETE');
  };

  const enAttente = (notes ?? []).filter((n) => etats[n.id] === 'attente' || estLocale(n.id)).length;

  return (
    <>
      <PageTitle
        title={
          <>
            Mon <em className="text-match-gradient font-serif italic font-medium">carnet</em>
          </>
        }
        subtitle="Qui vous avez rencontré, ce que vous vous êtes dit, et ce que vous avez promis de faire. Tout s’enregistre tout seul, même sans réseau."
      />

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <Btn variant="primary" onClick={ajouter}>
          <Plus size={14} strokeWidth={1.5} className="mr-1.5 inline" />
          Nouvelle rencontre
        </Btn>
        <a href="/api/carnet/export.csv" className="inline-flex items-center gap-1.5 text-[13px] text-fumee hover:text-blanc">
          <Download size={14} strokeWidth={1.5} /> Exporter en CSV
        </a>
        {enAttente > 0 && (
          <span className="text-[12px] text-fumee">
            {enAttente} note{enAttente > 1 ? 's' : ''} gardée{enAttente > 1 ? 's' : ''} sur cet appareil, en attente de réseau
          </span>
        )}
      </div>

      {aFaire.length > 0 && (
        <Card className="mb-5">
          <SectionLabel>À faire après vos rencontres</SectionLabel>
          <div className="mt-3 flex flex-col">
            {aFaire.slice(0, 8).map((a) => (
              <label key={a.id} className="flex cursor-pointer items-start gap-3 border-b border-graphite py-2 last:border-0">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 shrink-0 accent-[var(--em-accent)]"
                  onChange={() => {
                    setAFaire((l) => l.filter((x) => x.id !== a.id));
                    void envoyer(`/api/carnet/actions/${a.id}`, { faite: true });
                  }}
                />
                <span className="min-w-0">
                  <span className="block text-[14px] text-blanc">{a.texte}</span>
                  <span className="block text-[12px] text-fumee">
                    {[a.entreprise ?? a.nom, a.evenement_nom].filter(Boolean).join(' · ') || 'Sans contact'}
                    {a.echeance ? ` · avant le ${new Date(a.echeance).toLocaleDateString('fr-BE', { day: 'numeric', month: 'long' })}` : ''}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </Card>
      )}

      <div className="mb-5 flex flex-wrap gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search size={14} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-fumee" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Un nom, une entreprise, un mot de vos notes…"
            className="field-input !min-h-[40px] !py-2 !pl-9 !text-sm"
          />
        </div>
        <select
          value={filtreEvenement}
          onChange={(e) => setFiltreEvenement(e.target.value)}
          className="field-input !min-h-[40px] !w-auto !py-2 !text-sm"
        >
          <option value="" className="bg-charbon">Tous les événements</option>
          {evenements.map((e) => (
            <option key={e.id} value={e.id} className="bg-charbon">{e.nom}</option>
          ))}
        </select>
      </div>

      {filtreEvenement && (notes?.length ?? 0) > 0 && (
        <Card className="mb-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <SectionLabel>Clôturer cet événement</SectionLabel>
              <p className="mt-1.5 max-w-[520px] text-[13px] leading-relaxed text-fumee">
                Traitez vos {notes?.length} rencontre{(notes?.length ?? 0) > 1 ? 's' : ''} tant qu’elles sont fraîches :
                choisissez ce que vous proposez à chacun, l’IA écrit l’e-mail, vous corrigez.
              </p>
            </div>
            <Link to={`/compte/relance/${filtreEvenement}`} className="btn-creme !min-h-0 shrink-0 px-5 py-2 text-[14px]">
              Traiter les leads
            </Link>
          </div>
        </Card>
      )}

      {notes === null && <p className="text-[14px] text-fumee">Chargement…</p>}
      {notes?.length === 0 && (
        <Card>
          <p className="max-w-[560px] text-[14px] leading-relaxed text-fumee">
            Votre carnet est vide. Après chaque conversation, prenez trente secondes : le nom, l’entreprise,
            ce que vous vous êtes dit, et ce que vous avez promis d’envoyer. C’est ce qui transforme un salon
            en affaires.
          </p>
        </Card>
      )}

      <div className="flex flex-col gap-3">
        {notes?.map((n) => (
          <CarteNote
            key={n.id}
            note={n}
            evenements={evenements}
            etat={etats[n.id] ?? 'ok'}
            ouverte={ouverte === n.id}
            onOuvrir={() => setOuverte(ouverte === n.id ? null : n.id)}
            onChange={(patch) => modifier(n.id, patch)}
            onSupprimer={() => void supprimer(n.id)}
          />
        ))}
      </div>
    </>
  );
}

/* ————————————————— Une rencontre ————————————————— */

function CarteNote({
  note, evenements, etat, ouverte, onOuvrir, onChange, onSupprimer,
}: {
  note: NoteCarnet;
  evenements: EvenementCarnet[];
  etat: Etat;
  ouverte: boolean;
  onOuvrir: () => void;
  onChange: (patch: Partial<NoteCarnet>) => void;
  onSupprimer: () => void;
}) {
  const titre = note.nom?.trim() || note.entreprise?.trim() || 'Nouvelle rencontre';
  const restantes = (note.actions ?? []).filter((a) => !a.faite).length;
  const joignable = Boolean(note.email?.trim() || note.telephone?.trim() || note.linkedin?.trim());

  return (
    <Card className={ouverte ? 'border border-graphite' : ''}>
      <button onClick={onOuvrir} className="flex w-full items-start justify-between gap-3 text-left">
        <span className="min-w-0">
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-[16px] text-blanc">{titre}</span>
            {note.entreprise && note.nom && <span className="text-[13px] text-fumee">{note.entreprise}</span>}
            {!joignable && <Badge>Coordonnées manquantes</Badge>}
            {restantes > 0 && <Badge filled>{restantes} à faire</Badge>}
          </span>
          <span className="mt-1 block truncate text-[12px] text-fumee">
            {new Date(note.rencontre_le).toLocaleDateString('fr-BE', { day: 'numeric', month: 'long' })}
            {note.evenement_nom ? ` · ${note.evenement_nom}` : ''}
            {note.notes?.trim() ? ` · ${note.notes.trim().slice(0, 70)}${note.notes.trim().length > 70 ? '…' : ''}` : ''}
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-2">
          <EtatSauvegarde etat={etat} />
          <ChevronDown size={16} strokeWidth={1.5} className={`text-fumee transition-transform ${ouverte ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {ouverte && (
        <div className="mt-5 border-t border-graphite pt-5">
          {note.rappel && (
            <p className="mb-4 rounded-[8px] border border-graphite px-3 py-2 text-[13px] leading-relaxed text-blanc">
              {note.rappel}
            </p>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <Champ label="Nom" value={note.nom ?? ''} onChange={(v) => onChange({ nom: v })} placeholder="Sophie Bois" />
            <Champ label="Entreprise" value={note.entreprise ?? ''} onChange={(v) => onChange({ entreprise: v })} placeholder="Sophie Bois SRL" />
            <Champ label="Fonction" value={note.fonction ?? ''} onChange={(v) => onChange({ fonction: v })} placeholder="Gérante" />
            <label className="block">
              <span className="label-caps block text-fumee">Rencontré le</span>
              <input
                type="date"
                value={note.rencontre_le.slice(0, 10)}
                onChange={(e) => onChange({ rencontre_le: new Date(e.target.value || Date.now()).toISOString() })}
                className="field-input mt-2 !min-h-[42px] !py-2 !text-sm"
              />
            </label>
            <Champ label="E-mail" value={note.email ?? ''} onChange={(v) => onChange({ email: v })} placeholder="sophie@bois.be" type="email" />
            <Champ label="Téléphone" value={note.telephone ?? ''} onChange={(v) => onChange({ telephone: v })} placeholder="+32 470 12 34 56" type="tel" />
            <Champ label="LinkedIn" value={note.linkedin ?? ''} onChange={(v) => onChange({ linkedin: v })} placeholder="https://linkedin.com/in/…" />
            <Champ label="Site web" value={note.site_web ?? ''} onChange={(v) => onChange({ site_web: v })} placeholder="https://…" />
          </div>

          <label className="mt-4 block">
            <span className="label-caps block text-fumee">À quel événement</span>
            <select
              value={note.evenement_id ?? ''}
              onChange={(e) => onChange({ evenement_id: e.target.value || null })}
              className="field-input mt-2 !min-h-[42px] !py-2 !text-sm"
            >
              <option value="" className="bg-charbon">Hors événement</option>
              {evenements.map((e) => (
                <option key={e.id} value={e.id} className="bg-charbon">{e.nom}</option>
              ))}
            </select>
          </label>

          <label className="mt-4 block">
            <span className="label-caps block text-fumee">Vos notes</span>
            <textarea
              rows={5}
              value={note.notes ?? ''}
              onChange={(e) => onChange({ notes: e.target.value })}
              placeholder="Ce qu’il fait, ce qu’il cherche, ce qui vous a marqué, le nom de son associé…"
              className="field-input mt-2 !min-h-0 !py-2 !text-sm"
            />
          </label>

          <PhotosEtCarte note={note} onChange={onChange} />

          <Actions actions={note.actions ?? []} onChange={(actions) => onChange({ actions })} />

          <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-graphite pt-4">
            <div className="flex flex-wrap gap-3 text-[13px]">
              {note.email?.trim() && (
                <a href={`mailto:${note.email.trim()}`} className="inline-flex items-center gap-1.5 text-fumee hover:text-blanc">
                  <Mail size={13} strokeWidth={1.5} /> Écrire
                </a>
              )}
              {note.telephone?.trim() && (
                <a href={`tel:${note.telephone.replace(/\s/g, '')}`} className="inline-flex items-center gap-1.5 text-fumee hover:text-blanc">
                  <Phone size={13} strokeWidth={1.5} /> Appeler
                </a>
              )}
              {note.pseudo && (
                <a href={`/${note.pseudo}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-fumee hover:text-blanc">
                  <Link2 size={13} strokeWidth={1.5} /> Sa page <ArrowUpRight size={11} strokeWidth={1.5} />
                </a>
              )}
            </div>
            <button
              onClick={() => {
                if (confirm(`Supprimer définitivement la note sur ${titre} ?`)) onSupprimer();
              }}
              className="inline-flex items-center gap-1.5 text-[13px] text-fumee hover:text-blanc"
            >
              <Trash2 size={13} strokeWidth={1.5} /> Supprimer
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}

/**
 * Les photos de la rencontre, et la lecture de la carte de visite.
 *
 * La lecture ne part qu'une fois la photo réellement enregistrée côté serveur :
 * une note encore locale n'a pas d'identifiant à qui parler. On le dit plutôt
 * que de laisser un bouton sans effet.
 */
function PhotosEtCarte({ note, onChange }: { note: NoteCarnet; onChange: (patch: Partial<NoteCarnet>) => void }) {
  const [lecture, setLecture] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const lire = async () => {
    if (estLocale(note.id)) {
      setMessage('La photo n’est pas encore enregistrée — réessayez dans un instant.');
      return;
    }
    setLecture(true);
    setMessage(null);
    const r = await envoyer<{ lu: boolean; resume: string; note: NoteCarnet }>(`/api/carnet/notes/${note.id}/lire-carte`, {});
    setLecture(false);
    if (r.erreur || !r.donnees) {
      setMessage(r.erreur ?? 'La lecture a échoué.');
      return;
    }
    // On reprend la fiche telle que le serveur l'a complétée : seules les cases
    // vides ont bougé, ce que vous aviez tapé est intact.
    const s = r.donnees.note;
    onChange({
      nom: s.nom, entreprise: s.entreprise, fonction: s.fonction,
      email: s.email, telephone: s.telephone, linkedin: s.linkedin, site_web: s.site_web,
      carte_texte: s.carte_texte, carte_lue_le: s.carte_lue_le,
    });
    setMessage(r.donnees.resume);
  };

  return (
    <>
      <PhotosRencontre
        photos={(note.photos ?? []) as PhotoNote[]}
        onChange={(photos) => onChange({ photos })}
        onLire={() => void lire()}
        lectureEnCours={lecture}
      />
      {message && <p className="mt-2 text-[13px] leading-relaxed text-blanc">{message}</p>}
      {note.carte_texte && (
        <details className="mt-2">
          <summary className="cursor-pointer text-[12px] text-fumee">Texte lu sur la carte</summary>
          <p className="mt-1 whitespace-pre-wrap text-[12px] leading-relaxed text-fumee">{note.carte_texte}</p>
        </details>
      )}
    </>
  );
}

function Actions({ actions, onChange }: { actions: ActionNote[]; onChange: (a: ActionNote[]) => void }) {
  const [saisie, setSaisie] = useState('');
  const ajouter = () => {
    const t = saisie.trim();
    if (!t) return;
    onChange([...actions, { texte: t, echeance: null, faite: false }]);
    setSaisie('');
  };
  return (
    <div className="mt-5">
      <SectionLabel>Prochaines actions</SectionLabel>
      <div className="mt-2 flex flex-col">
        {actions.map((a, i) => (
          <div key={i} className="flex items-center gap-3 border-b border-graphite py-2 last:border-0">
            <input
              type="checkbox"
              checked={Boolean(a.faite)}
              onChange={(e) => onChange(actions.map((x, j) => (j === i ? { ...x, faite: e.target.checked } : x)))}
              className="h-4 w-4 shrink-0 accent-[var(--em-accent)]"
            />
            <input
              value={a.texte}
              onChange={(e) => onChange(actions.map((x, j) => (j === i ? { ...x, texte: e.target.value } : x)))}
              className={`field-input !min-h-[34px] flex-1 !border-0 !py-1 !text-sm ${a.faite ? 'text-fumee line-through' : ''}`}
            />
            <input
              type="date"
              value={a.echeance ?? ''}
              onChange={(e) => onChange(actions.map((x, j) => (j === i ? { ...x, echeance: e.target.value || null } : x)))}
              className="field-input !min-h-[34px] !w-auto !py-1 !text-sm"
            />
            <button onClick={() => onChange(actions.filter((_, j) => j !== i))} aria-label="Retirer" className="text-fumee hover:text-blanc">
              <X size={14} strokeWidth={1.5} />
            </button>
          </div>
        ))}
      </div>
      <div className="mt-2 flex gap-2">
        <input
          value={saisie}
          onChange={(e) => setSaisie(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              ajouter();
            }
          }}
          placeholder="Lui envoyer le devis, l’ajouter sur LinkedIn…"
          className="field-input !min-h-[38px] flex-1 !py-1.5 !text-sm"
        />
        <Btn onClick={ajouter}>Ajouter</Btn>
      </div>
    </div>
  );
}

function EtatSauvegarde({ etat }: { etat: Etat }) {
  if (etat === 'envoi') return <span className="text-[11px] text-fumee">Enregistrement…</span>;
  if (etat === 'attente')
    return (
      <span className="flex items-center gap-1 text-[11px] text-fumee" title="Gardé sur cet appareil, renvoyé dès que le réseau revient">
        <NotebookPen size={11} strokeWidth={1.5} /> sur cet appareil
      </span>
    );
  if (etat === 'ok') return <Check size={13} strokeWidth={1.5} className="text-fumee" />;
  return null;
}
